import type { ReactElement } from 'react';
import type { DocumentSectionType } from '@shared/types';
import { PageShell, PageTitle } from '../Page';
import type { PageProps } from '../types';
import { EditableBullets, EditableText } from '../../components/Editable';
import { brandNameFor } from '../../lib/theme';
import { useBindings } from '../../state/BindingsContext';
import { useContentLibrary } from '../../state/ContentLibraryContext';
import { Frame, SignatureLine } from './primitives';

/**
 * Replaces `{{client}}` in the library's terms copy with the advertiser
 * name. The shipped library text (config/content-library.json) has no
 * tokens today (Library Phase 3, design.md V15 Streams, rewrites it to carry
 * client + validity merge) — this is forward-compatible with that and a
 * no-op on today's plain text.
 */
function mergeClientToken(text: string, clientName: string): string {
  return text.replace(/\{\{\s*client\s*\}\}/gi, clientName);
}

/**
 * terms (brief §4 `18`): FULL light, text + signature block. Signatories are
 * ALWAYS client then agency (design.md V15 "Bugs in the SUNY deck this model
 * prevents": "Both signature lines say 'SUNY Adirondack Signature.'
 * Signatories are always client + agency" — never the client twice).
 * `content.body` wins over the merged library text when the rep has written
 * their own, the same precedence every library-backed field in this app
 * follows.
 */
export function TermsPage({ proposal, section, actions, readOnly }: PageProps): ReactElement {
  const resolve = useBindings();
  const { advertiser } = proposal;
  const clientName = advertiser.name || 'Client';
  const { library } = useContentLibrary();
  const fallback = library.staticSections[section.type as DocumentSectionType];
  const body = section.content.body ?? (fallback ? mergeClientToken(fallback.body, clientName) : undefined);
  const agencyName = brandNameFor(proposal.document?.theme, 'Agency');
  const fullTermsLink = section.content.fields?.fullTermsLink;
  const hasLowerBullets = (section.content.bullets?.length ?? 0) > 0;

  return (
    <PageShell
      section={section}
      readOnly={readOnly}
      onTitleSave={(title) => actions.saveTitle(section, title)}
      className="mv3-terms-root"
      hideTitle
    >
      <Frame surface="light" proposal={proposal} section={section}>
        <div className="mv3-terms-body">
          <PageTitle
            title={section.title}
            readOnly={readOnly}
            onSave={(title) => actions.saveTitle(section, title)}
            className="mv3-h1 mv3-terms-h1"
          />
          <h2 className="mv3-terms-client">{clientName}</h2>
          <div className="mv3-terms-copy">
            <EditableText
              value={body}
              field="body"
              placeholder="Add terms"
              readOnly={readOnly}
              variant="rationale"
              onSave={(next) => actions.saveContent(section, { body: next })}
            />
          </div>
          <div className="mv3-terms-hairline" aria-hidden="true" />
          <div className="mv3-terms-signatures">
            <SignatureLine label={`${clientName} Signature`} className="mv3-terms-sig-wide" />
            <SignatureLine label="Date" className="mv3-terms-sig-narrow" />
            <SignatureLine label={`${agencyName} Signature`} className="mv3-terms-sig-wide" />
            <SignatureLine label="Date" className="mv3-terms-sig-narrow" />
          </div>
          {hasLowerBullets || !readOnly ? (
            <div className="mv3-terms-lower">
              <EditableBullets
                value={section.content.bullets}
                readOnly={readOnly}
                placeholder="Add full terms text, one point per line"
                onSave={(next) => actions.saveContent(section, { bullets: next })}
              />
            </div>
          ) : null}
          {!hasLowerBullets && fullTermsLink ? (
            <p className="mv3-terms-lower-link">Full terms: {resolve(fullTermsLink)}</p>
          ) : null}
        </div>
      </Frame>
    </PageShell>
  );
}
